import { useEffect, useMemo } from 'react';

import { IntegrationSystem, SelectOption } from '@/shared/types';

import { BlockTitle, FormContent, FormElement } from '@/components/old';

import { SERVICE_CODES } from '@/lib/consts';

import { FormDocument, FormAddress } from '@/api/types';

import { AddressBlock } from '../../../../../../../components/Forms/AddressBlock';
import { allowedForSelectionDocumentTypesForDeclaredPlaceOfResidence } from '../../../../../../../components/Forms/constants';
import { DocumentsData } from '../../../../../../../components/Forms/DocumentsData';
import { PlaceOfResidenceFormDataProps } from '../../../../../../../components/Forms/Steps/PlaceOfResidence';
import InputDate from '../../../../../../../components/Inputs/InputDate';
import { AddressCheckAlertAddressType } from '../../../../../../../components/Modals/AddressCheckAlert';
import { StepsNavigationName } from '../../../../../../../config/services';
import { useAsyncDataLoader } from '../../../../../../../hooks/useAsyncDataLoader';
import { getERZDocumentTypeEntites } from '../../../../../helpers';
import {
  todaysDate,
  currentServiceCode,
  oneDayFromToday,
  sixMonthsFromToday,
} from '../../TemporaryResidenceRelocation';

import { AddressCheckStatus, AddressMapping, ADRESSES_MAPPING, DeclaredAddressNewFieldName } from './types';

interface Props {
  formData: {
    [DeclaredAddressNewFieldName.declaredAddressNew]?: string;
    [DeclaredAddressNewFieldName.issuedResidence]?: string;
    [DeclaredAddressNewFieldName.expiredResidence]?: string;
    [DeclaredAddressNewFieldName.declaredAddressNewDocuments]?: Array<FormDocument<IntegrationSystem.ERZ>>;
  } & PlaceOfResidenceFormDataProps;
  setAddressCheckStatus: (checkStatus: AddressCheckStatus) => void;
  handleInputChange: (name: string, value: unknown) => void;
  handleClearInput: (name: string) => void;
}

const Step5: React.FC<Props> = ({ formData, handleInputChange, setAddressCheckStatus, handleClearInput }) => {
  const { data: documentTypeEntities = [], load } = useAsyncDataLoader(
    () => getERZDocumentTypeEntites(allowedForSelectionDocumentTypesForDeclaredPlaceOfResidence),
    { defaultDataValue: [] },
  );

  const declaredAddressNew = useMemo<FormAddress>(
    () => JSON.parse(formData.declaredAddressNew || '{}'),
    [formData.declaredAddressNew],
  );
  const issuedResidence = useMemo(
    () => (formData.issuedResidence ? new Date(formData.issuedResidence) : null),
    [formData.issuedResidence],
  );
  const expiredResidence = useMemo(
    () => (formData.expiredResidence ? new Date(formData.expiredResidence) : null),
    [formData.expiredResidence],
  );

  const minAndMaxExpiredResidenceDates = useMemo<Record<'minDate' | 'maxDate', Date>>(() => {
    const date = issuedResidence ?? todaysDate;

    const minDate = new Date(date);
    minDate.setMonth(date.getMonth() + 1);

    const maxDate = new Date(date);
    maxDate.setMonth(date.getMonth() + 6);

    return {
      minDate,
      maxDate,
    };
  }, [issuedResidence]);

  useEffect(() => {
    function parseJsonOrFallback(input = '') {
      try {
        return JSON.parse(input);
      } catch (error) {
        return input;
      }
    }

    const checkIsAddressEqual = (addressMapping: AddressMapping): boolean =>
      Object.entries(addressMapping).every(([addressFieldName, newAddressFieldName]) => {
        const addressFieldValue = parseJsonOrFallback(formData[addressFieldName as keyof typeof formData] as string);
        const newAddressFieldValue = declaredAddressNew[newAddressFieldName];

        return (!newAddressFieldValue && !addressFieldValue) || typeof newAddressFieldValue === 'object'
          ? (newAddressFieldValue as SelectOption)?.id === addressFieldValue?.id
          : (newAddressFieldValue as string)?.toString() === addressFieldValue?.toString();
      });

    setAddressCheckStatus(
      Object.entries(ADRESSES_MAPPING).reduce(
        (acc, [addressType, addressMapping]) => {
          const isAddressEqual = checkIsAddressEqual(addressMapping);

          return isAddressEqual
            ? {
                isError: isAddressEqual,
                addressType: addressType as AddressCheckAlertAddressType,
              }
            : acc;
        },
        {
          isError: false,
          addressType: null,
        } as AddressCheckStatus,
      ),
    );
  }, [declaredAddressNew]);

  return (
    <>
      <BlockTitle id="step-description" paddingBottomMdExtra bgLight>
        {currentServiceCode === SERVICE_CODES.RESIDENCE_RELOCATION
          ? StepsNavigationName.newAddressOfResidence
          : StepsNavigationName.newTemporaryAddressOfResidence}
      </BlockTitle>

      <FormContent>
        <AddressBlock
          onlyUkraine
          title={
            currentServiceCode === SERVICE_CODES.RESIDENCE_RELOCATION
              ? StepsNavigationName.newAddressOfResidence
              : StepsNavigationName.newTemporaryAddressOfResidence
          }
          stringifiedAddress={formData[DeclaredAddressNewFieldName.declaredAddressNew] ?? ''}
          handleAddressChange={(name, newAddress): void => handleInputChange(name, newAddress)}
          name={DeclaredAddressNewFieldName.declaredAddressNew}
          handleInputChange={handleInputChange}
          handleClearInput={handleClearInput}
        />

        <DocumentsData
          documentsRequired
          withFileUpload
          documentTypeLabel={
            currentServiceCode === SERVICE_CODES.RESIDENCE_RELOCATION
              ? 'Тип документа, що підтверджує нову адресу місця проживання (зберігання зброї)'
              : 'Тип документа, що підтверджує адресу тимчасового місця зберігання зброї'
          }
          documentUploadLabel={
            currentServiceCode === SERVICE_CODES.RESIDENCE_RELOCATION
              ? 'Документ, що підтверджує нову адресу місця проживання (зберігання зброї)'
              : 'Документ, що підтверджує адресу тимчасового місця зберігання зброї'
          }
          documentTypeEntities={documentTypeEntities ?? []}
          maxDocumentsCount={6}
          documents={formData[DeclaredAddressNewFieldName.declaredAddressNewDocuments] ?? []}
          onDocumentTypeSelectOpen={async (): Promise<void> => load()}
          handleChange={(documents): void =>
            handleInputChange(DeclaredAddressNewFieldName.declaredAddressNewDocuments, documents)
          }
          name={DeclaredAddressNewFieldName.declaredAddressNewDocuments}
          handleInputChange={handleInputChange}
          handleClearInput={handleClearInput}
        />

        {currentServiceCode === SERVICE_CODES.TEMPORARY_RESIDENCE_RELOCATION && (
          <>
            <FormElement variant="title" />

            <InputDate
              required
              minDate={oneDayFromToday}
              maxDate={sixMonthsFromToday}
              label="Дата початку дії дозволу"
              name={DeclaredAddressNewFieldName.issuedResidence}
              value={issuedResidence}
              onChange={(date): void => {
                handleInputChange(DeclaredAddressNewFieldName.issuedResidence, date);
                handleClearInput(DeclaredAddressNewFieldName.expiredResidence);
              }}
            />

            <InputDate
              required
              minDate={minAndMaxExpiredResidenceDates.minDate}
              maxDate={minAndMaxExpiredResidenceDates.maxDate}
              label="Строк тимчасової зміни місця зберігання зброї"
              name={DeclaredAddressNewFieldName.expiredResidence}
              disabled={!issuedResidence}
              value={expiredResidence}
              onChange={(date): void => handleInputChange(DeclaredAddressNewFieldName.expiredResidence, date)}
            />
          </>
        )}
      </FormContent>
    </>
  );
};

export default Step5;
